import { useEffect, useMemo, useState } from "react"

import { formatNumber } from "accounting"
import { Col, Flex, Row, Spin, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import DashboardCard from "../../../Components/DashboardCard"
import EmptyComponent from "../../../Components/Empty"
import DropdownBlockCheckbox from "../../../Components/Filters/DropdownBlockCheckbox"
import Private from "../../../Components/Private"
import TableComponent from "../../../Components/Table"
import { RoutesPaths } from "../../../constants/user-routes"
import Icon from "../../../Icon"
import { DEFAULT_VISIBLE_FILTERS } from "../../DisputesPage/pages/AllPage/constants/columns"
import {
  resetFilters,
  setFilters as setDisputeFilters,
  setDisputeVisibleFilters
} from "../../DisputesPage/redux/disputes.slice"
import AnalyticsFilters from "../components/AnalyticsFilters"
import Section from "../components/Section"
import { sortableTitle } from "../components/sortableTitle"
import SummaryRows from "../components/SummaryRows/SummaryRows"
import Toggle from "../components/Toggle"
import TrendsLineChart, { LINE_COLORS } from "../components/TrendsLineChart/TrendsLineChart"
import { useAnalyticsScope } from "../hooks/useAnalyticsScope"
import { ANALYTICS_SECTION } from "../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../redux/analytics.slice"

import "../../DashboardPage/styles.less"
import styles from "../analytics.module.less"
import tables from "../tables.module.less"

const { Title, Text } = Typography

const TOOLTIP_ID = "chartjs-tooltip_breakdown"
// How many reason codes the chart plots before you pick your own: more lines than this and
// the legend stops being readable. Nothing stops you selecting more.
const DEFAULT_CODE_LINES = 8

// Two-level breakdown table: a row per category, expandable into the reason codes inside
// it. Both levels carry the same columns, so a category and the codes under it are read
// with one pair of eyes. Movement lives in the chart above, not in here.
const breakdownColumns = (t) => [
  {
    title: sortableTitle(t("Category and reason code"), "label"),
    dataIndex: "label",
    key: "label",
    render: (_value, record) =>
      record.level === "category" ? (
        <Flex align="center" gap={8} style={{ minWidth: 0 }}>
          <span className={styles.dot} style={{ background: record.color }} aria-hidden="true" />
          <Text strong ellipsis={{ tooltip: record.label }}>
            {record.label}
          </Text>
          <Text type="secondary">
            {(record.children?.length || 0) === 1
              ? t("1 code")
              : t("_count_ codes", { count: record.children?.length || 0 })}
          </Text>
        </Flex>
      ) : (
        <Flex vertical gap={2} style={{ minWidth: 0 }}>
          <Text style={{ fontVariantNumeric: "tabular-nums" }}>{record.label}</Text>
          {record.description ? (
            <Text type="secondary" ellipsis={{ tooltip: record.description }}>
              {record.description}
            </Text>
          ) : null}
        </Flex>
      )
  },
  {
    title: sortableTitle(t("Disputes"), "count", { align: "right" }),
    dataIndex: "count",
    key: "count",
    width: 120,
    align: "right",
    defaultSortOrder: "descend",
    sorter: (a, b) => a.count - b.count,
    render: (value) => formatNumber(value)
  },
  {
    title: sortableTitle(t("Value ($)"), "value", { align: "right" }),
    dataIndex: "value",
    key: "value",
    width: 160,
    align: "right",
    sorter: (a, b) => a.value - b.value,
    render: (value) => <Private>{`$${formatNumber(value, 2, ",")}`}</Private>
  },
  {
    // Null win rate (nothing resolved here yet) renders as a dash rather than a
    // misleading 0%, the same rule the win-rate matrix uses.
    title: sortableTitle(t("Win rate"), "winRate", { align: "right" }),
    dataIndex: "winRate",
    key: "winRate",
    width: 130,
    align: "right",
    // Nulls sort below 0%, so a descending sort never leads with rows that have no rate.
    sorter: (a, b) => (a.winRate ?? -1) - (b.winRate ?? -1),
    render: (value, record) =>
      value == null ? (
        <Text type="secondary">-</Text>
      ) : (
        <Text>{`${formatNumber(value, 0, ",")}% (${formatNumber(record.won)}/${formatNumber(record.resolved)})`}</Text>
      )
  },
  {
    // Same Share column as the country breakdown: the percentage also paints the progress
    // underline behind the row (getDashboardProgressRowProps reads `percentage`).
    title: sortableTitle(t("Share"), "percentage", { align: "right" }),
    dataIndex: "percentage",
    key: "percentage",
    width: 110,
    align: "right",
    sorter: (a, b) => (a.percentage || 0) - (b.percentage || 0),
    render: (value) => `${formatNumber(value ?? 0, 1, ",", ".")}%`
  }
]

// The summary's cells under the numeric columns: disputes, value, win rate, share.
const SUMMARY_CELLS = [
  { align: "right", render: (s) => formatNumber(s.count) },
  { align: "right", render: (s) => <Private>{`$${formatNumber(s.amount, 2, ",")}`}</Private> },
  {
    align: "right",
    render: (s) =>
      s.win_rate == null
        ? "-"
        : `${formatNumber(s.win_rate, 0, ",")}% (${formatNumber(s.won_count)}/${formatNumber(s.resolved_count)})`
  },
  null
]

// The breakdown behind the "Category trends over time" card: both levels of the same
// population on one page.
//
// The chart owns the choices about itself: what to plot (categories or reason codes),
// which of them, and by count or by dollar volume. Nothing above the chart narrows the
// page, so the table underneath is always the full breakdown for the period, and the
// numbers in it always reconcile with the summary cards.
//
// The table is the numbers, the chart is the movement. That split is why the table carries
// no sparkline of its own: two answers to the same question, one of them too small to read.
const ReasonCodesFullPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getBase, getCompare } = useAnalyticsScope()
  const byCategory = useSelector(selectSectionData(ANALYTICS_SECTION.BY_CATEGORY))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.BY_CATEGORY))
  const trend = useSelector(selectSectionData(ANALYTICS_SECTION.REASON_CODES_TREND))
  const trendFetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.REASON_CODES_TREND))
  const theme = useSelector((state) => state.nav.theme)
  const isMobile = useSelector((state) => state.nav.isMobile)

  // What the chart plots: one line per category, or one per reason code.
  const [level, setLevel] = useState("category")
  // Count or dollar volume, the same choice the card this page opens from offers.
  const [metric, setMetric] = useState("count")
  // Which series to plot. Empty means "all of them" for categories; for reason codes the
  // default is the busiest few (see DEFAULT_CODE_LINES), since every code at once is
  // unreadable. Kept per level so switching back and forth does not lose a selection.
  const [catKeys, setCatKeys] = useState([])
  const [codeKeys, setCodeKeys] = useState([])

  const isCodeLevel = level === "code"

  useEffect(() => {
    getCompare(ANALYTICS_SECTION.BY_CATEGORY)
  }, [getCompare])

  // Codes are asked for by name (the busiest few when none are picked): every code's line would
  // be most of the answer. Categories come whole and are narrowed below without a request.
  const codeKeysParam = isCodeLevel && codeKeys.length ? codeKeys.join(",") : null
  useEffect(() => {
    getBase(ANALYTICS_SECTION.REASON_CODES_TREND, {
      level,
      ...(codeKeysParam ? { keys: codeKeysParam } : {})
    })
  }, [getBase, level, codeKeysParam])

  const summary = byCategory?.summary
  const categories = useMemo(() => byCategory?.result ?? [], [byCategory])

  // Colour per category by its rank over the whole period (the order by-category/ sends, the
  // donut's order), not by its position in the current view, so a category keeps its colour
  // as the chart is filtered and the table re-sorted.
  const colorOf = useMemo(
    () => new Map(categories.map((cat, i) => [cat.category, LINE_COLORS[i % LINE_COLORS.length]])),
    [categories]
  )

  // Every code present, busiest first: the options for the chart's own code filter.
  const codeOptions = useMemo(() => {
    const byCode = new Map()
    categories
      .flatMap((cat) => cat.codes)
      .sort((a, b) => b.count - a.count)
      .forEach((code) => {
        if (byCode.has(code.reason_code)) return
        byCode.set(
          code.reason_code,
          code.description ? `${code.reason_code} · ${code.description}` : code.reason_code
        )
      })
    return [...byCode].map(([id, name]) => ({ id, name }))
  }, [categories])

  const chartSeries = useMemo(() => {
    const series = (trend?.series ?? []).map((s) => ({
      key: s.key,
      label: isCodeLevel ? s.label : t(s.label),
      data: metric === "value" ? s.amount : s.count,
      color: isCodeLevel ? undefined : colorOf.get(s.key)
    }))
    return isCodeLevel || !catKeys.length ? series : series.filter((s) => catKeys.includes(s.key))
  }, [trend, isCodeLevel, metric, catKeys, colorOf, t])

  const tableRows = useMemo(
    () =>
      categories.map((cat) => {
        const children = cat.codes.map((code) => ({
          key: code.reason_code,
          label: code.reason_code,
          description: code.description,
          level: "code",
          rowKey: `code:${cat.category}:${code.reason_code}`,
          count: code.count,
          value: code.amount,
          winRate: code.win_rate,
          won: code.won_count,
          resolved: code.resolved_count,
          percentage: code.share
        }))
        return {
          key: cat.category,
          label: t(cat.label),
          level: "category",
          rowKey: `cat:${cat.category}`,
          color: colorOf.get(cat.category) || LINE_COLORS[0],
          count: cat.count,
          value: cat.amount,
          winRate: cat.win_rate,
          won: cat.won_count,
          resolved: cat.resolved_count,
          percentage: cat.share,
          children: children.length ? children : undefined
        }
      }),
    [categories, colorOf, t]
  )

  const cards = useMemo(
    () => [
      { title: "Total disputes", value: summary?.count ?? 0, icon: "receipt_check" },
      { title: "Total value", value: summary?.amount ?? 0, prefix: "$", icon: "money" },
      { title: "Reason codes", value: summary?.codes_count ?? 0, icon: "square_lines" },
      { title: "Most common code", value: summary?.top_reason_code || "-", icon: "pulse" }
    ],
    [summary]
  )

  const columns = useMemo(() => breakdownColumns(t), [t])

  // Row click: a code opens the disputes list filtered to it. resetFilters first so a stale
  // filter cannot narrow the list further than the row promises. The Reason code chip is
  // hidden on that page by default, so it is added to the visible set as well: a list
  // narrowed by a filter you cannot see (or clear) reads as broken. Category rows expand
  // instead of navigating: their codes are the drill-down.
  const openCode = (code) => {
    dispatch(resetFilters())
    dispatch(setDisputeFilters({ reason_code: [code] }))
    if (!DEFAULT_VISIBLE_FILTERS.includes("reason_code")) {
      dispatch(setDisputeVisibleFilters([...DEFAULT_VISIBLE_FILTERS, "reason_code"]))
    }
    navigate(RoutesPaths.recover.all)
  }

  useEffect(() => {
    document.title = "Categories and reason codes - Chargeback"
    document.getElementById("global-wrap")?.scrollTo(0, 0)
  }, [])

  // Everything that shapes the chart sits in the chart's own header: what to plot, which
  // ones, and in what unit. The reason-code list is long, so that filter gets a search box.
  const chartControls = (
    <Flex align="center" gap={12} wrap="wrap" className={styles.trendHead}>
      <Toggle
        value={level}
        onChange={setLevel}
        options={[
          { value: "category", label: "Category" },
          { value: "code", label: "Reason code" }
        ]}
      />
      {isCodeLevel ? (
        <DropdownBlockCheckbox
          id="reason_code"
          label={t("Codes")}
          iconType="square_lines"
          type="chart"
          allBtnText={t("Top _count_", { count: DEFAULT_CODE_LINES })}
          withSearch
          sortDisabled
          data={codeOptions}
          dropdownFilters={codeKeys.length ? codeKeys : "all"}
          setDropdownFilters={(obj) => {
            const v = obj?.reason_code
            setCodeKeys(v === "all" || v == null ? [] : Array.isArray(v) ? v : [v])
          }}
        />
      ) : (
        <DropdownBlockCheckbox
          id="category_group"
          label={t("Categories")}
          iconType="square_lines"
          type="chart"
          allBtnText={t("All categories")}
          sortDisabled
          data={categories.map((cat) => ({ id: cat.category, name: t(cat.label) }))}
          dropdownFilters={catKeys.length ? catKeys : "all"}
          setDropdownFilters={(obj) => {
            const v = obj?.category_group
            setCatKeys(v === "all" || v == null ? [] : Array.isArray(v) ? v : [v])
          }}
        />
      )}
      <Toggle
        value={metric}
        onChange={setMetric}
        options={[
          { value: "count", label: "By count" },
          { value: "value", label: "By volume" }
        ]}
      />
    </Flex>
  )

  return (
    <Row gutter={[0, isMobile ? 12 : 32]} className="dashboard-page">
      <Col span={24}>
        <ButtonComponent
          type="text"
          onClick={() => navigate(RoutesPaths.recover.analytics)}
          icon={<Icon type="chevron_left_small" />}
          style={{ marginLeft: -12 }}
        >
          {t("Back to Analytics")}
        </ButtonComponent>
      </Col>

      <Col span={24}>
        <Flex
          justify="space-between"
          align={!isMobile && "flex-start"}
          style={{ flexDirection: isMobile && "column" }}
        >
          <div style={{ paddingBottom: isMobile && 24 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              {t("Categories and reason codes")}
            </Title>
            <Text type="secondary">
              {t(
                "How your disputes move over time, by category or by reason code, and the full breakdown underneath. Only codes raised on your disputes in this period are listed."
              )}
            </Text>
          </div>

          <AnalyticsFilters />
        </Flex>
      </Col>

      <Col span={24}>
        <Row gutter={[24, 24]}>
          {cards.map((card) => (
            <Col key={card.title} xs={24} sm={12} lg={6}>
              <DashboardCard data={card} theme={theme} />
            </Col>
          ))}
        </Row>
      </Col>

      <Col span={24}>
        <Section
          title={isCodeLevel ? t("Reason code trends over time") : t("Category trends over time")}
          tooltip={
            isCodeLevel
              ? t(
                  "One line per reason code, by number of disputes or by dollar volume. Lines are code numbers: the table below names each one. The busiest codes are plotted until you pick your own."
                )
              : t(
                  "One line per dispute category, by number of disputes or by dollar volume. Switch to Reason code to plot the individual codes instead."
                )
          }
          extra={chartControls}
        >
          <Spin spinning={trendFetching}>
            <TrendsLineChart
              labels={trend?.labels}
              unit={trend?.unit}
              series={chartSeries}
              metric={metric}
              tooltipId={TOOLTIP_ID}
            />
          </Spin>
        </Section>
      </Col>

      <Col span={24}>
        {/* The platform's Table (Components/Table), not antd's: it stamps `custom-table`,
            the Chargeback Olympics look - boxed grey header row with rounded ends and a
            separator under every row. Columns and cell contents are unchanged; only the
            progress bar that used to be painted behind each row is gone, since that table
            does not have one. */}
        <TableComponent
          className={cls(tables.breakdown, tables.olympics)}
          dataSource={tableRows}
          columns={columns}
          expandable={{
            // Clicking a category row opens its codes; only code rows navigate.
            expandRowByClick: true,
            indentSize: 24
          }}
          onRow={(record) => ({
            onClick: record.level === "code" ? () => openCode(record.key) : undefined,
            style: { cursor: "pointer" }
          })}
          loading={fetching}
          withOutPagination
          scroll={{}}
          rowKey="rowKey"
          tableLayout="fixed"
          showHeader={!!tableRows.length}
          summary={() => (
            <SummaryRows
              summary={tableRows.length ? summary : null}
              comparisonSummary={byCategory?.comparison_summary}
              comparisonLabel={byCategory?.comparison_period?.label}
              cells={SUMMARY_CELLS}
            />
          )}
          locale={{
            emptyText: <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
          }}
        />
      </Col>
    </Row>
  )
}

export default ReasonCodesFullPage
